import { type FlightDocumentType } from '../FlightDocuments/FlightDocument';
import { type FlightLegPhase } from '../FlightLegs/FlightLeg';
import { type DbSaveable } from '../LocalDB/types';

// The status every expense starts in. The card payment rules treat an expense that still carries it,
// with no receipt, no reason and no document, as untouched.
export { EXPENSE_STATUS_UNRESOLVED } from '@eflight/shared/dist/cardPayments';

// The converter lambda's work queue: it polls expenses carrying this while one of their files has
// no media file yet.
export const MEDIA_FILES_NOT_CREATED = 'x';

export type FlightExpenseContent = {
    key: string;
    type: string;
};

export interface FlightExpenseRecord extends DbSaveable {
    id: string;
    createdAt: Date;
    aircraftId?: string;
    flightId?: string;
    legId?: string;
    phase?: FlightLegPhase;
    bankTransactionId?: string;
    contentKeys: FlightExpenseContent[];
    mediaFilesNotCreated?: string;
    title?: string | null;
    caption?: string | null;
    documentType?: FlightDocumentType;
    name?: string;
    amount?: number | null;
    currency?: string | null;
    paymentMethod?: string;
    paymentProvider?: string | null;
    reason?: string;
    reasonNote?: string | null;
    noDocumentReason?: string | null;
    noDocumentNote?: string | null;
    includes?: string[];
    expenseMonth?: string;
    status?: string;
}

// The bank owns amount, currency and payment method of a linked expense, and the pilot cannot
// delete it.
export const isBankLinked = (record: FlightExpenseRecord): boolean => Boolean(record.bankTransactionId);

// Every device holds the expenses of all pilots; the server names the creator as the owner once
// the create has synced.
export const isOwnedByUser = (record: FlightExpenseRecord, userId: string): boolean =>
    (record.editableByUserIds ?? []).includes(userId);

// The pilot declared the category had no cost at all, so such a record owes nothing to any check.
export const EXPENSE_STATUS_NOT_APPLICABLE = 'not_applicable';

export const EXPENSE_PAYMENT_METHOD_PERSONAL_CARD = 'personal_card';
export const EXPENSE_PAYMENT_METHOD_CASH = 'cash';
export const EXPENSE_PAYMENT_METHOD_WIRE_TRANSFER = 'wire_transfer';
export const EXPENSE_PAYMENT_METHOD_PAID_BY_CUSTOMER = 'paid_by_customer';
export const EXPENSE_PAYMENT_METHOD_FUEL_CARD = 'fuel_card';
export const EXPENSE_PAYMENT_METHOD_ON_HANDLING_INVOICE = 'on_handling_invoice';
export const EXPENSE_PAYMENT_METHOD_ON_CONTRACT = 'on_contract';
